import { useEffect } from 'react';

import { Stack, Alert, Typography } from '@mui/material';

import useLocales from 'src/hooks/useLocales';

import { WorkflowStepDto, WorkflowStepAssigneeOverrides } from 'src/@types/workflow/types';

import AssigneeAutocomplete from './AssigneeAutocomplete';
import { parseConfig, resolveStepKind } from './stepKindContract';
import { STEP_KINDS, StepKindIcon, stepColorPath } from './stepKind';
import useOverridableWorkflowSteps from './hooks/useOverridableWorkflowSteps';

type WorkflowStepAssigneeOverrideFieldsProps = {
  definitionNumber: number | '';
  value: WorkflowStepAssigneeOverrides;
  onChange: (value: WorkflowStepAssigneeOverrides) => void;
  onValidityChange?: (isValid: boolean) => void;
};

const resolveAssigneeEmail = (step: WorkflowStepDto, overrides: WorkflowStepAssigneeOverrides) =>
  overrides[step.id] ?? step.assigneeEmail ?? '';

export default function WorkflowStepAssigneeOverrideFields({
  definitionNumber,
  value,
  onChange,
  onValidityChange,
}: WorkflowStepAssigneeOverrideFieldsProps) {
  const { t } = useLocales();
  const { overridableSteps, isLoadingOverridableSteps, hasOverridableStepsLoadFailed } =
    useOverridableWorkflowSteps(definitionNumber);

  useEffect(() => {
    const cannotYetConfirmRequiredFields =
      definitionNumber !== '' && (isLoadingOverridableSteps || hasOverridableStepsLoadFailed);

    if (cannotYetConfirmRequiredFields) {
      onValidityChange?.(false);

      return;
    }

    const isValid = overridableSteps.every((step) =>
      Boolean(resolveAssigneeEmail(step, value).trim())
    );

    onValidityChange?.(isValid);
  }, [
    definitionNumber,
    isLoadingOverridableSteps,
    hasOverridableStepsLoadFailed,
    overridableSteps,
    value,
    onValidityChange,
  ]);

  if (isLoadingOverridableSteps || definitionNumber === '') {
    return null;
  }

  if (hasOverridableStepsLoadFailed) {
    return <Alert severity="error">{t('workflows.stepAssigneeOverridesLoadFailed')}</Alert>;
  }

  if (overridableSteps.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {overridableSteps.map((step) => {
        const kind = resolveStepKind(step.stepTypeId, parseConfig(step.config));
        const kindInfo = kind ? STEP_KINDS[kind] : null;
        const hasDefault = Boolean(step.assigneeEmail?.trim());
        const current = resolveAssigneeEmail(step, value);

        return (
          <Stack key={step.id} spacing={0.75}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              {kind && (
                <StepKindIcon kind={kind} fontSize="small" sx={{ color: stepColorPath(kind) }} />
              )}
              <Typography variant="subtitle2">
                {t('workflows.stepOrderLabel', { order: step.order })}
                {' · '}
                {kindInfo ? t(kindInfo.labelKey) : step.stepType}
              </Typography>
            </Stack>

            <AssigneeAutocomplete
              required={!hasDefault}
              error={hasDefault && !current.trim()}
              label={t(kindInfo?.emailLabelKey ?? 'workflows.fields.assignee')}
              helperText={t(
                hasDefault
                  ? 'workflows.stepAssigneeOverrideHint'
                  : 'workflows.stepAssigneeRequiredHint'
              )}
              value={current}
              onChange={(assigneeEmail) => onChange({ ...value, [step.id]: assigneeEmail })}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}
